
import { useState, useEffect } from 'react';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export const SidebarSettings = () => {
  const [sidebarIconSize, setSidebarIconSize] = useState(1.25);
  const [sidebarFontScale, setSidebarFontScale] = useState(1);
  const [menuSpacing, setMenuSpacing] = useState(0.5);
  const [showIcons, setShowIcons] = useState(true);

  useEffect(() => {
    const savedIconSize = localStorage.getItem('sidebar-icon-size');
    const savedFontScale = localStorage.getItem('sidebar-font-scale');
    const savedMenuSpacing = localStorage.getItem('menu-spacing');
    const savedShowIcons = localStorage.getItem('sidebar-show-icons');

    if (savedIconSize) setSidebarIconSize(parseFloat(savedIconSize));
    if (savedFontScale) setSidebarFontScale(parseFloat(savedFontScale));
    if (savedMenuSpacing) setMenuSpacing(parseFloat(savedMenuSpacing));
    if (savedShowIcons) setShowIcons(savedShowIcons === 'true');
  }, []);

  const updateSidebarIconSize = (value: number[]) => {
    const size = value[0];
    setSidebarIconSize(size);
    document.documentElement.style.setProperty('--sidebar-icon-size', `${size}rem`);
    localStorage.setItem('sidebar-icon-size', size.toString());
  };

  const updateSidebarFontScale = (value: number[]) => {
    const scale = value[0];
    setSidebarFontScale(scale);
    document.documentElement.style.setProperty('--sidebar-font-scale', scale.toString());
    localStorage.setItem('sidebar-font-scale', scale.toString());
  };

  const updateMenuSpacing = (value: number[]) => {
    const spacing = value[0];
    setMenuSpacing(spacing);
    document.documentElement.style.setProperty('--menu-spacing', `${spacing}rem`);
    localStorage.setItem('menu-spacing', spacing.toString());
  };

  const toggleIcons = (checked: boolean) => {
    setShowIcons(checked);
    localStorage.setItem('sidebar-show-icons', checked.toString());
    window.location.reload();
  };

  return (
    <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
      <CardHeader className="border-b border-gradient-to-r from-transparent via-[hsl(var(--tab-sidebar))]/20 to-transparent">
        <CardTitle className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-[hsl(var(--tab-sidebar))] to-[hsl(var(--tab-sidebar))]/80 shadow-lg shadow-[hsl(var(--tab-sidebar))]/30">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>
          <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">إعدادات الشريط الجانبي</span>
        </CardTitle>
        <CardDescription className="text-base">تخصيص مظهر وحجم عناصر الشريط الجانبي</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 p-6">
        <div className="space-y-3 p-4 bg-gradient-to-br from-muted/20 to-muted/10 rounded-xl border border-border/50">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">إظهار الأيقونات</Label>
            <Switch checked={showIcons} onCheckedChange={toggleIcons} />
          </div>
          <p className="text-sm text-muted-foreground">
            سيتم إعادة تحميل الصفحة عند تغيير هذا الإعداد
          </p>
        </div>

        <div className="space-y-3 p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20">
          <Label className="text-base font-medium">حجم الأيقونات: {sidebarIconSize.toFixed(2)}</Label>
          <Slider
            value={[sidebarIconSize]}
            onValueChange={updateSidebarIconSize}
            min={0.8}
            max={2.5}
            step={0.1}
            className="w-full"
          />
        </div>

        <div className="space-y-3 p-4 bg-gradient-to-br from-success/5 to-success/10 rounded-xl border border-success/20">
          <Label className="text-base font-medium">حجم خط القائمة: {sidebarFontScale.toFixed(2)}</Label>
          <Slider
            value={[sidebarFontScale]}
            onValueChange={updateSidebarFontScale}
            min={0.7}
            max={1.8}
            step={0.1}
            className="w-full"
          />
        </div>

        <div className="space-y-3 p-4 bg-gradient-to-br from-warning/5 to-warning/10 rounded-xl border border-warning/20">
          <Label className="text-base font-medium">المسافة بين عناصر القائمة: {menuSpacing.toFixed(2)}</Label>
          <Slider
            value={[menuSpacing]}
            onValueChange={updateMenuSpacing}
            min={0.1}
            max={1.5}
            step={0.1}
            className="w-full"
          />
          <p className="text-sm text-muted-foreground">
            تحكم في المسافة بين عناصر القائمة الرئيسية
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
